import type { ChangeEvent } from 'react';
import emojiRegex from 'emoji-regex';
import React, { memo, useEffect, useRef, useState } from '../../lib/teact/teact';

import type { ApiWalletTransactionCategory, ApiWalletTransactionCounterparty } from '../../api/types';

import buildClassName from '../../util/buildClassName';
import { isValidAddress } from '../../util/isValidAddress';
import { METADATA_CATEGORY_COLORS } from '../../util/walletMetadataDirectory';
import { callApiWithThrow } from '../../api';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import useMetadataAuthorization, { MetadataAuthorizationCancelled } from '../../hooks/useMetadataAuthorization';
import { notifyWalletMetadataDirectoryChanged } from '../../hooks/useWalletMetadataDirectory';

import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import CounterpartyAvatar, { primeCounterpartyAvatar } from './CounterpartyAvatar';
import MetadataEmojiPicker from './MetadataEmojiPicker';

import settingsStyles from '../settings/Settings.module.scss';
import styles from '../settings/SettingsMetadata.module.scss';
import inputStyles from '../ui/Input.module.scss';
import modalStyles from '../ui/Modal.module.scss';
import identityStyles from './MetadataIdentity.module.scss';

export type MetadataRecordKind = 'counterparty' | 'category';
type RecordValue = ApiWalletTransactionCounterparty | ApiWalletTransactionCategory;
type Props = {
  kind: MetadataRecordKind;
  accountId: string;
  record?: RecordValue;
  initialAddresses?: string[];
  onSaved: (record: RecordValue) => void;
  onCancel?: NoneToVoidFunction;
  onBusyChange?: (isBusy: boolean) => void;
  hideDeleteAction?: boolean;
  isDeleteConfirmationOpen?: boolean;
  onDeleteConfirmationChange?: (isOpen: boolean) => void;
};
type Errors = { name?: string; addresses?: Record<number, string>; photo?: string; form?: string };
const COLOR_NAMES = ['rose', 'peach', 'yellow', 'green', 'mint', 'blue', 'lavender', 'pink'];
const DEFAULT_EMOJI = '\uD83C\uDFF7\uFE0F';
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

function MetadataRecordEditor({
  kind, accountId, record, initialAddresses, onSaved, onCancel, onBusyChange, hideDeleteAction,
  isDeleteConfirmationOpen, onDeleteConfirmationChange,
}: Props) {
  const lang = useLang();
  const { runWithAuthorization, authorizationModal } = useMetadataAuthorization(accountId);
  const counterparty = kind === 'counterparty' ? record as ApiWalletTransactionCounterparty | undefined : undefined;
  const category = kind === 'category' ? record as ApiWalletTransactionCategory | undefined : undefined;
  const [name, setName] = useState(record?.name ?? '');
  const [addresses, setAddresses] = useState(counterparty?.addresses?.slice() ?? initialAddresses?.slice() ?? ['']);
  const [emoji, setEmoji] = useState(category?.emoji ?? DEFAULT_EMOJI);
  const [color, setColor] = useState(category?.color ?? METADATA_CATEGORY_COLORS[5]);
  const [avatar, setAvatar] = useState<{ content: Uint8Array; content_type: string }>();
  const [previewUrl, setPreviewUrl] = useState<string>();
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLocallyConfirmingDelete, setIsLocallyConfirmingDelete] = useState(false);
  const [isChoosingEmoji, setIsChoosingEmoji] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const fileInputRef = useRef<HTMLInputElement>();
  const operationRef = useRef(false);
  const photoRevisionRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => () => {
    mountedRef.current = false;
    photoRevisionRef.current += 1;
  }, []);
  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const clearError = useLastCallback((field: keyof Errors) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  });
  const setBusy = useLastCallback((busy: boolean) => {
    operationRef.current = busy;
    setIsSaving(busy);
    onBusyChange?.(busy);
  });
  const isConfirmingDelete = isDeleteConfirmationOpen ?? isLocallyConfirmingDelete;
  const setIsConfirmingDelete = useLastCallback((isOpen: boolean) => {
    if (isDeleteConfirmationOpen === undefined) setIsLocallyConfirmingDelete(isOpen);
    onDeleteConfirmationChange?.(isOpen);
  });
  const closeDeleteConfirmation = useLastCallback(() => {
    if (!operationRef.current) setIsConfirmingDelete(false);
  });
  const handlePhoto = useLastCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!file.size || file.size > MAX_AVATAR_BYTES) {
      setErrors((current) => ({ ...current, photo: lang('$metadata_avatar_size') }));
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setErrors((current) => ({ ...current, photo: lang('$metadata_avatar_type') }));
      return;
    }
    const revision = ++photoRevisionRef.current;
    const url = URL.createObjectURL(file);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      if (image.naturalWidth * image.naturalHeight > 16_000_000) throw new Error('image_size');
      const content = new Uint8Array(await file.arrayBuffer());
      if (revision !== photoRevisionRef.current || !mountedRef.current) {
        URL.revokeObjectURL(url);
        return;
      }
      setAvatar({ content, content_type: file.type });
      setPreviewUrl(url);
      setRemoveAvatar(false);
      clearError('photo');
    } catch {
      URL.revokeObjectURL(url);
      if (revision === photoRevisionRef.current && mountedRef.current) {
        setErrors((current) => ({ ...current, photo: lang('$metadata_avatar_type') }));
      }
    }
  });
  const save = useLastCallback(async () => {
    if (operationRef.current) return;
    const next: Errors = {};
    if (!name.trim()) next.name = lang('$metadata_name_required');
    else if (name.trim().length > 120) next.name = lang('$metadata_name_invalid');
    if (kind === 'category' && emojiRegex().exec(emoji)?.[0] !== emoji) next.name = lang('$metadata_emoji_invalid');
    if (kind === 'counterparty') {
      const seen = new Set<string>();
      const invalid: Record<number, string> = {};
      (addresses.length ? addresses : ['']).forEach((address, index) => {
        const trimmed = address.trim();
        if (!trimmed) invalid[index] = lang('$metadata_address_required');
        else if ((!isValidAddress(trimmed, 'tron') && !/^41[\da-f]{40}$/i.test(trimmed)) || seen.has(trimmed)) {
          invalid[index] = lang('$metadata_address_invalid');
        }
        seen.add(trimmed);
      });
      if (Object.keys(invalid).length) next.addresses = invalid;
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      const saved = await runWithAuthorization<RecordValue>(() => kind === 'counterparty'
        ? callApiWithThrow('saveWalletCounterparty', accountId,
          { id: record?.id, name, addresses }, avatar, removeAvatar)
        : callApiWithThrow('saveWalletCategory', accountId, { id: record?.id, name, emoji, color }));
      if (!mountedRef.current) return;
      if (kind === 'counterparty' && avatar) {
        primeCounterpartyAvatar(saved as ApiWalletTransactionCounterparty, avatar);
      }
      notifyWalletMetadataDirectoryChanged({ accountId, record: saved });
      setBusy(false);
      onSaved(saved);
    } catch (cause) {
      if (!mountedRef.current || cause instanceof MetadataAuthorizationCancelled) return;
      const message = cause instanceof Error ? cause.message : '';
      const code = (cause as { code?: string })?.code;
      setErrors(code === 'metadata_addresses' || message.includes('address')
        ? { addresses: Object.fromEntries(addresses.map((_, index) => [index, lang('$metadata_address_invalid')])) }
        : code === 'metadata_emoji' ? { name: lang('$metadata_emoji_invalid') }
          : code === 'metadata_name' ? { name: lang('$metadata_name_invalid') }
            : code === 'metadata_avatar' ? { photo: lang('$metadata_avatar_type') }
              : { form: lang('$metadata_local_save_failed') });
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  });
  const remove = useLastCallback(async () => {
    if (!record?.id || operationRef.current) return;
    setBusy(true);
    try {
      const method = kind === 'counterparty' ? 'deleteWalletCounterparty' : 'deleteWalletCategory';
      await runWithAuthorization(() => callApiWithThrow(method, accountId, record.id));
      if (!mountedRef.current) return;
      notifyWalletMetadataDirectoryChanged({ accountId, removed: { kind, id: record.id } });
      setBusy(false);
      onCancel?.();
    } catch (cause) {
      if (mountedRef.current && !(cause instanceof MetadataAuthorizationCancelled)) {
        setErrors({ form: lang('$metadata_local_save_failed') });
      }
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  });
  const avatarIdentity: ApiWalletTransactionCounterparty = {
    ...counterparty, id: record?.id ?? 'new', name, normalized_name: '', owner_address: '', updated_at: '',
    account_id: accountId, avatar_version: removeAvatar ? undefined : counterparty?.avatar_version,
  };

  return (
    <div data-telemetry-block="">
      {kind === 'counterparty' && (
        <div className={styles.photo}>
          <div className={styles.photoControls}>
            <button
              type="button"
              className={styles.photoButton}
              title={lang('$metadata_change_photo')}
              aria-label={lang('$metadata_change_photo')}
              disabled={isSaving}
              onClick={() => fileInputRef.current?.click()}
            >
              <CounterpartyAvatar counterparty={avatarIdentity} size="large" previewUrl={previewUrl} isPlaceholder />
              <i className={buildClassName(styles.photoBadge, 'icon-plus')} aria-hidden />
            </button>
            {(previewUrl || (!removeAvatar && counterparty?.avatar_version)) && (
              <button
                type="button"
                className={styles.photoRemoveButton}
                disabled={isSaving}
                title={lang('$metadata_remove_photo')}
                aria-label={lang('$metadata_remove_photo')}
                onClick={() => {
                  photoRevisionRef.current += 1;
                  setAvatar(undefined);
                  setPreviewUrl(undefined);
                  setRemoveAvatar(true);
                }}
              >
                <i className="icon-trash-small" aria-hidden />
              </button>
            )}
          </div>
          {errors.photo && (
            <p className={buildClassName(inputStyles.label, inputStyles.error, styles.fieldError)} role="alert">
              {errors.photo}
            </p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className={styles.fileInput}
            onChange={handlePhoto}
          />
        </div>
      )}
      <Input
        id="metadata-name"
        label={lang('Name')}
        value={name}
        maxLength={120}
        isDisabled={isSaving}
        error={errors.name}
        wrapperClassName={styles.metadataInput}
        className={kind === 'category' ? styles.categoryName : undefined}
        onInput={(value) => {
          setName(value);
          clearError('name');
          clearError('form');
        }}
      >
        {kind === 'category' && (
          <button
            type="button"
            className={styles.emojiAddon}
            disabled={isSaving}
            title={lang('$metadata_choose_emoji')}
            aria-label={lang('$metadata_choose_emoji')}
            onClick={() => setIsChoosingEmoji(true)}
          >
            {emoji}
          </button>
        )}
      </Input>
      {kind === 'counterparty' ? (
        <>
          <h3 className={settingsStyles.blockTitle}>{lang('Addresses')}</h3>
          {(addresses.length ? addresses : ['']).map((address, index) => (
            <div className={styles.addressRow} key={index}>
              <Input
                id={`counterparty-address-${index}`}
                value={address}
                placeholder={lang('Wallet address')}
                maxLength={66}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect={false}
                isDisabled={isSaving}
                error={errors.addresses?.[index]}
                wrapperClassName={styles.metadataInput}
                onInput={(value) => {
                  setAddresses((current) => (current.length ? current : [''])
                    .map((item, i) => i === index ? value : item));
                  setErrors((current) => ({
                    ...current, form: undefined, addresses: { ...current.addresses, [index]: '' },
                  }));
                }}
              />
              {addresses.length > 1 && (
                <button
                  type="button"
                  className={styles.iconButton}
                  title={lang('Remove')}
                  aria-label={lang('Remove')}
                  disabled={isSaving}
                  onClick={() => {
                    setAddresses(addresses.filter((_, i) => i !== index));
                    clearError('addresses');
                  }}
                >
                  <i className="icon-trash-small" aria-hidden />
                </button>
              )}
            </div>
          ))}
          {addresses.length < 20 && (
            <Button
              isSimple
              isText
              isDisabled={isSaving}
              onClick={() => {
                setAddresses([...addresses, '']);
                clearError('addresses');
              }}
            >
              <i className="icon-plus" aria-hidden /> {lang('$metadata_add_address')}
            </Button>
          )}
        </>
      ) : (
        <>
          <h3 className={settingsStyles.blockTitle}>{lang('Color')}</h3>
          <div className={styles.swatches} role="group" aria-label={lang('Color')}>
            {METADATA_CATEGORY_COLORS.map((value, index) => (
              <button
                key={value}
                type="button"
                className={buildClassName(styles.swatch, identityStyles[`pastel${index}`])}
                disabled={isSaving}
                aria-pressed={color.toUpperCase() === value}
                title={lang(`$metadata_color_${COLOR_NAMES[index]}`)}
                aria-label={lang(`$metadata_color_${COLOR_NAMES[index]}`)}
                onClick={() => setColor(value)}
              >
                {color.toUpperCase() === value && <i className="icon-check" aria-hidden />}
              </button>
            ))}
          </div>
          {isChoosingEmoji && (
            <MetadataEmojiPicker
              isOpen
              onClose={() => setIsChoosingEmoji(false)}
              onSelect={(value) => {
                setEmoji(value);
                clearError('name');
              }}
            />
          )}
        </>
      )}
      {errors.form && !isConfirmingDelete && (
        <p className={buildClassName(inputStyles.label, inputStyles.error, styles.fieldError)} role="alert">
          {errors.form}
        </p>
      )}
      <div className={styles.actions}>
        {onCancel && <Button isSecondary isDisabled={isSaving} onClick={onCancel}>{lang('Cancel')}</Button>}
        <Button isPrimary isLoading={isSaving} onClick={save}>{lang('Save')}</Button>
      </div>
      {record?.id && !hideDeleteAction && !isConfirmingDelete && (
        <div className={styles.recordDeleteAction}>
          <Button
            isText
            isDestructive
            isDisabled={isSaving}
            className={styles.recordDeleteButton}
            onClick={() => setIsConfirmingDelete(true)}
          >
            <i className="icon-trash-small" aria-hidden />
            {lang('Delete')}
          </Button>
        </div>
      )}
      <Modal isOpen={isConfirmingDelete} isCompact onClose={closeDeleteConfirmation}>
        <p className={modalStyles.text}>
          {lang(kind === 'counterparty' ? '$metadata_delete_counterparty' : '$metadata_delete_category')}
        </p>
        {errors.form && (
          <p className={buildClassName(inputStyles.label, inputStyles.error, styles.fieldError)} role="alert">
            {errors.form}
          </p>
        )}
        <div className={modalStyles.buttons}>
          <Button
            isSecondary
            isDisabled={isSaving}
            className={modalStyles.button}
            onClick={closeDeleteConfirmation}
          >
            {lang('Cancel')}
          </Button>
          <Button isDestructive isLoading={isSaving} className={modalStyles.button} onClick={remove}>
            {lang('Delete')}
          </Button>
        </div>
      </Modal>
      {authorizationModal}
    </div>
  );
}

export default memo(MetadataRecordEditor);
